import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  todaySP,
  type Account,
  type AdjustBalanceBody,
  type Transaction,
  type Ymd,
} from '@lira/shared';
import { apiPost } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { toast } from '@/stores/toast';
import { MoneyInput, MoneyText, Sheet } from '@/components/ui';
import { cn } from '@/lib/cn';

interface BalanceAdjustModalProps {
  open: boolean;
  onClose: () => void;
  account: Account | null;
}

/**
 * Reajuste de saldo (doc 12 / tela Contas): informa o saldo desejado e uma data;
 * o backend cria uma transação de ajuste (ignore_in_reports=1) que leva o saldo atual ao alvo.
 */
export function BalanceAdjustModal({ open, onClose, account }: BalanceAdjustModalProps) {
  const queryClient = useQueryClient();
  const [absCents, setAbsCents] = useState(0);
  const [negative, setNegative] = useState(false);
  const [date, setDate] = useState<Ymd>(todaySP());

  // (re)inicializa ao abrir com o saldo atual da conta como ponto de partida
  useEffect(() => {
    if (!open || !account) return;
    setAbsCents(Math.abs(account.currentBalanceCents));
    setNegative(account.currentBalanceCents < 0);
    setDate(todaySP());
  }, [open, account]);

  const targetCents = negative ? -absCents : absCents;
  const deltaCents = account ? targetCents - account.currentBalanceCents : 0;

  const mutation = useMutation({
    mutationFn: () => {
      const body: AdjustBalanceBody = { targetBalanceCents: targetCents, date };
      return apiPost<{ transaction: Transaction }>(`/accounts/${account!.id}/adjustment`, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      void queryClient.invalidateQueries({ queryKey: ['transactions'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast('Saldo reajustado.', 'success');
      onClose();
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const noChange = deltaCents === 0;
  const canSave = !!account && !noChange && !mutation.isPending;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSave) return;
    mutation.mutate();
  };

  return (
    <Sheet open={open} onClose={onClose} title="Reajuste de saldo" maxWidth={440}>
      <form onSubmit={handleSubmit} className="space-y-5">
        {account && (
          <div className="flex items-center justify-between rounded-lg bg-canvas px-4 py-3">
            <span className="text-[13px] text-muted">Saldo atual de {account.name}</span>
            <MoneyText cents={account.currentBalanceCents} colorBySign bold />
          </div>
        )}

        <div className="rounded-lg bg-canvas px-4 py-4">
          <label className="mb-1 block text-[13px] text-muted">Novo saldo</label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setNegative((v) => !v)}
              aria-label={negative ? 'Tornar positivo' : 'Tornar negativo'}
              aria-pressed={negative}
              className={cn(
                'grid size-9 shrink-0 place-items-center rounded-full border text-lg font-bold transition-colors',
                negative
                  ? 'border-expense bg-expense-weak text-expense'
                  : 'border-border text-muted hover:border-muted hover:text-ink',
              )}
            >
              {negative ? '−' : '+'}
            </button>
            <MoneyInput
              valueCents={absCents}
              onChange={setAbsCents}
              accent={negative ? 'expense' : 'income'}
              size="lg"
              aria-label="Novo saldo"
            />
          </div>
        </div>

        <div>
          <label htmlFor="adjust-date" className="mb-1 block text-[13px] text-muted">
            Data do ajuste
          </label>
          <input
            id="adjust-date"
            type="date"
            value={date}
            onChange={(e) => e.target.value && setDate(e.target.value)}
            className="h-11 w-full rounded-lg border border-field bg-surface px-3 text-[15px] text-ink transition-colors focus:border-primary focus:outline-none"
          />
        </div>

        {!noChange && (
          <p className="flex items-center justify-between rounded-lg bg-primary-weak px-4 py-2.5 text-[13px]">
            <span className="text-muted">Lançamento de ajuste</span>
            <MoneyText cents={deltaCents} colorBySign bold />
          </p>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="h-11 rounded-full px-5 text-sm font-semibold uppercase tracking-wide text-muted transition-colors hover:bg-canvas hover:text-ink"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="flex h-11 items-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            Salvar
          </button>
        </div>
      </form>
    </Sheet>
  );
}
